import React, { useEffect, useState } from "react";
import { Link, router } from "@inertiajs/react";
import {
    Search,
    ChevronDown,
    ChevronUp,
    BookOpen,
    HelpCircle,
    Newspaper,
    ArrowLeft,
    Calendar,
} from "lucide-react";

// Simple accordion — mirrors the visual language of the site's shared
// FaqSection.jsx but lets each section on this page own its own <h2>,
// which the shared component doesn't allow.
const FaqAccordion = ({ items }) => {
    const [openIndex, setOpenIndex] = useState(null);

    if (!items || items.length === 0) return null;

    return (
        <div className="space-y-3">
            {items.map((faq, index) => (
                <div
                    key={index}
                    className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                        openIndex === index
                            ? "bg-primary-50/50 border-primary-100 shadow-sm"
                            : "bg-white border-gray-200 hover:border-primary-200"
                    }`}
                >
                    <button
                        type="button"
                        onClick={() => setOpenIndex(openIndex === index ? null : index)}
                        className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-right"
                        aria-expanded={openIndex === index}
                    >
                        <span className={`font-semibold text-sm sm:text-base leading-relaxed ${openIndex === index ? "text-primary-700" : "text-gray-800"}`}>
                            {faq.question}
                        </span>
                        <span className={`p-1 rounded-full shrink-0 ${openIndex === index ? "bg-primary-100" : "bg-gray-100"}`}>
                            {openIndex === index ? (
                                <ChevronUp className="w-4 h-4 text-primary-600" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                            )}
                        </span>
                    </button>
                    <div
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                            openIndex === index ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
                        }`}
                    >
                        <div className="p-4 sm:p-5 pt-0 text-sm text-gray-600 leading-relaxed border-t border-primary-100/50">
                            {faq.answer}
                            {faq.source_url && (
                                <Link
                                    href={faq.source_url}
                                    className="mt-2 inline-flex items-center gap-1 text-primary-600 font-bold hover:text-primary-700"
                                >
                                    {faq.source_label}
                                    <ArrowLeft className="w-3.5 h-3.5" />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Real <a href> page links (/faq?page=N) so every page of questions is crawlable.
const FaqPager = ({ page, totalPages, search }) => {
    if (totalPages <= 1) return null;
    const href = (n) => {
        const q = new URLSearchParams();
        if (search) q.set("search", search);
        if (n > 1) q.set("page", String(n));
        const qs = q.toString();
        return `/faq${qs ? `?${qs}` : ""}`;
    };
    return (
        <nav aria-label="صفحات الأسئلة" className="mt-6 flex flex-wrap items-center justify-center gap-2 text-sm font-bold">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <a
                    key={n}
                    href={href(n)}
                    aria-current={n === page ? "page" : undefined}
                    className={`w-10 h-10 inline-flex items-center justify-center rounded-full border ${n === page ? "bg-primary-600 border-primary-600 text-white" : "border-gray-200 text-gray-700 hover:border-primary-600 hover:text-primary-600"}`}
                >
                    {n}
                </a>
            ))}
            {page < totalPages && (
                <a href={href(page + 1)} rel="next" className="h-10 px-4 inline-flex items-center rounded-full border border-gray-200 text-gray-700 hover:border-primary-600 hover:text-primary-600">
                    التالي
                </a>
            )}
        </nav>
    );
};

const KnowledgeCenter = ({ totalCount, faqs, popularFaqs, categories, latestArticles, search: initialSearch, page = 1, totalPages = 1, filteredCount }) => {
    const [searchTerm, setSearchTerm] = useState(initialSearch || "");

    useEffect(() => {
        const delay = setTimeout(() => {
            if (searchTerm !== (initialSearch || "")) {
                router.get(
                    "/faq",
                    searchTerm ? { search: searchTerm } : {},
                    { preserveState: true, preserveScroll: true, replace: true },
                );
            }
        }, 500);
        return () => clearTimeout(delay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const isSearching = Boolean(initialSearch);

    return (
        <div className="wrapper py-10 sm:py-16">
            {/* Hero / H1 */}
            <div className="text-center max-w-3xl mx-auto">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-xs sm:text-sm font-bold text-primary-600">
                    <BookOpen className="w-4 h-4" />
                    مركز المعرفة
                </span>
                <h1 className="mt-4 text-2xl sm:text-4xl font-black text-gray-900 leading-snug">
                    الأسئلة الشائعة ومركز المعرفة
                </h1>
                <h2 className="mt-4 text-base sm:text-xl font-bold text-primary-600">
                    أكثر من {totalCount.toLocaleString("ar-EG")} سؤال وجواب
                </h2>
                <p className="mt-3 text-sm sm:text-base text-gray-500 leading-relaxed">
                    اعثر على إجابات شاملة لأكثر الأسئلة شيوعًا حول خدمات ويز فريلانس، التسويق الإلكتروني، SEO، المتاجر
                    الإلكترونية، الإعلانات، وآلية تنفيذ المشاريع.
                </p>
            </div>

            {/* Search — H2 */}
            <div className="mt-10 max-w-2xl mx-auto">
                <h2 className="sr-only">ابحث عن إجابتك</h2>
                <div className="relative">
                    <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="ابحث عن إجابتك... مثال: كم تكلفة تصميم موقع؟"
                        className="w-full rounded-2xl border border-gray-200 bg-white py-4 pr-12 pl-4 text-sm sm:text-base focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/10"
                    />
                </div>
            </div>

            {isSearching ? (
                <div className="mt-10 max-w-3xl mx-auto">
                    <h2 className="mb-6 text-lg sm:text-2xl font-black text-gray-900">
                        نتائج البحث عن «{initialSearch}» ({(filteredCount ?? faqs.length).toLocaleString("ar-EG")})
                    </h2>
                    {faqs.length > 0 ? (
                        <>
                            <FaqAccordion items={faqs} />
                            <FaqPager page={page} totalPages={totalPages} search={initialSearch} />
                        </>
                    ) : (
                        <p className="text-center text-gray-500 py-10">لا توجد نتائج مطابقة، جرّب كلمات أخرى.</p>
                    )}
                </div>
            ) : (
                <>
                    {/* أدلة التسويق الإلكتروني — H2 */}
                    {categories.length > 0 && (
                        <section className="mt-14 max-w-4xl mx-auto">
                            <h2 className="mb-6 text-lg sm:text-2xl font-black text-gray-900 text-center">
                                أدلة التسويق الإلكتروني
                            </h2>
                            <div className="flex flex-wrap justify-center gap-2.5">
                                {categories.map((cat) => (
                                    <Link
                                        key={cat}
                                        href={`/blogs?category=${encodeURIComponent(cat)}`}
                                        className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                                    >
                                        {cat}
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* الأسئلة الأكثر شيوعًا — H2 */}
                    <section className="mt-14 max-w-3xl mx-auto">
                        <h2 className="mb-6 flex items-center justify-center gap-2 text-lg sm:text-2xl font-black text-gray-900">
                            <HelpCircle className="w-5 h-5 text-primary-600" />
                            {page > 1 ? `الأسئلة الشائعة - صفحة ${page}` : "الأسئلة الأكثر شيوعًا"}
                        </h2>
                        <FaqAccordion items={faqs.length ? faqs : popularFaqs} />
                        <FaqPager page={page} totalPages={totalPages} />
                    </section>

                    {/* أحدث الأدلة والمقالات — H2 */}
                    {latestArticles.length > 0 && (
                        <section className="mt-14 max-w-6xl mx-auto">
                            <div className="mb-6 flex items-center justify-between">
                                <h2 className="flex items-center gap-2 text-lg sm:text-2xl font-black text-gray-900">
                                    <Newspaper className="w-5 h-5 text-primary-600" />
                                    أحدث الأدلة والمقالات
                                </h2>
                                <Link
                                    href="/blogs"
                                    className="flex items-center gap-1.5 text-sm font-bold text-primary-600 hover:text-primary-700"
                                >
                                    عرض الكل
                                    <ArrowLeft className="w-4 h-4" />
                                </Link>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                                {latestArticles.map((article) => (
                                    <Link
                                        key={article.id}
                                        href={`/blogs/${article.slug || article.id}`}
                                        className="group block rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <span
                                            className={`inline-block rounded-full px-3 py-1 text-[11px] font-bold text-white ${article.category_color || "bg-primary-600"}`}
                                        >
                                            {article.category}
                                        </span>
                                        <h3 className="mt-3 text-sm font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-primary-600">
                                            {article.title}
                                        </h3>
                                        <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {article.publish_date
                                                ? new Date(article.publish_date).toLocaleDateString("ar-EG", {
                                                      day: "numeric",
                                                      month: "long",
                                                      year: "numeric",
                                                  })
                                                : ""}
                                        </p>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
};

export default KnowledgeCenter;
